import { useEffect, useState } from "react";
import { Phone, Mail } from "lucide-react";
import PhoneAuthNative from "./PhoneAuthNative";
import EmailPasswordAuthNative from "./EmailPasswordAuthNative";

/**
 * AuthTabs — small Phone | Email tab switcher wrapping the two native
 * auth components. Phone is the default so the primary CTA on the
 * landing page and Follow modal is OTP-based sign-in.
 *
 * Props:
 *   redirectTo   — passed through to child (default `/app`)
 *   onSuccess    — passed through to PhoneAuthNative (skips navigation)
 *   initialMode  — 'login' | 'signup' (default 'login')
 *   defaultTab   — 'phone' | 'email' (default 'phone')
 *   compact      — trims helper copy in embedded contexts
 */
export default function AuthTabs({
  redirectTo = "/app",
  onSuccess,
  initialMode = "signup",
  defaultTab = "phone",
  compact = false,
  phoneEnabled = true,
  emailEnabled = true,
  onModeChange,
}) {
  // v146.13 — When Super Admin has disabled a method, force-select the
  // remaining one and hide the tab bar entirely so the UI feels
  // native, not "one option greyed out".
  const effectiveDefault =
    (defaultTab === "phone" && !phoneEnabled) ? "email" :
    (defaultTab === "email" && !emailEnabled) ? "phone" :
    defaultTab;
  const [tab, setTab] = useState(effectiveDefault);
  // v145.39 — Persist the login/signup mode ACROSS tab switches.
  // Previously each child (Phone / Email) held its own mode state, so
  // toggling to "Sign in" on Phone and then switching to Email would
  // silently re-mount Email in signup mode. Lifting `mode` to the
  // parent keeps the user in Sign in / Create-account regardless of
  // which channel they choose.
  const normalizeMode = (m) => (m === "signin" ? "login" : m);
  const [mode, setModeState] = useState(normalizeMode(initialMode));
  // v147.26 — Sync when the parent flips initialMode after a navigation
  // (e.g. user taps "Sign in instead" on /signup → /signin). AuthPage
  // stays mounted across those two routes because both render the same
  // element, so without this effect the child stays stuck on "signup"
  // and the Email form keeps demanding a two-password sign-up flow.
  useEffect(() => {
    const target = normalizeMode(initialMode);
    setModeState((cur) => (cur === target ? cur : target));
  }, [initialMode]);
  // v146.89 — Bubble mode changes up to AuthPage so the top-level Terms
  // gate can hide itself when the user toggles to Sign-in inside the tabs.
  const setMode = (m) => {
    const n = normalizeMode(m);
    setModeState(n);
    onModeChange?.(n);
  };

  const showTabBar = phoneEnabled && emailEnabled;

  return (
    <div className="w-full">
      {showTabBar && (
        <div
          role="tablist"
          aria-label="Sign in method"
          className="grid grid-cols-2 gap-0 border-2 border-ink mb-3"
          data-testid="auth-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "phone"}
            data-testid="auth-tab-phone"
            onClick={() => setTab("phone")}
            className={`px-4 py-3 font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 border-r-2 border-ink transition-colors ${
              tab === "phone" ? "bg-ink text-white" : "bg-white text-ink hover:bg-cream"
            }`}
          >
            <Phone className="w-4 h-4" strokeWidth={3} /> Phone
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "email"}
            data-testid="auth-tab-email"
            onClick={() => setTab("email")}
            className={`px-4 py-3 font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
              tab === "email" ? "bg-ink text-white" : "bg-white text-ink hover:bg-cream"
            }`}
          >
            <Mail className="w-4 h-4" strokeWidth={3} /> Email
          </button>
        </div>
      )}
      {tab === "phone" && phoneEnabled ? (
        <PhoneAuthNative
          redirectTo={redirectTo}
          onSuccess={onSuccess}
          initialMode={mode}
          onModeChange={setMode}
          compact={compact}
        />
      ) : tab === "email" && emailEnabled ? (
        <EmailPasswordAuthNative
          redirectTo={redirectTo}
          onSuccess={onSuccess}
          initialMode={mode}
          onModeChange={setMode}
        />
      ) : null}
    </div>
  );
}
